import { useEffect, useState, useMemo, useRef } from "react";
import { Container, Navbar, Row, Card, Button } from 'react-bootstrap'
import * as label from '../../shared/util/label'
import { ErrorBoundary } from 'react-error-boundary'
import { Column, DateTimePicker, Select } from '../../components';
import Logger from '../../shared/services/Logger';
import Grid from '../../components/components/Grid/Grid';
import { UserStatsData } from '../model/UserStatsData';
import StatisticsDataService from './StatisticsDataService';
import ExportToCsv from '../../shared/components/ExportToCSV/ExportToCsv';
import { CommonUtility } from '../../Services/CommonUtility';
interface IUserStatsGridState {
    userStats: UserStatsData[],
    gridColumns: any[]
}

var sampleGridData: UserStatsData[] = [];

const UserStatisticsDashboard = () => {
    const [fromDate, setFromDate] = useState(new Date());
    const [toDate, setToDate] = useState(new Date());
    const [fromDateInvalid, setFromDateInvalid] = useState(false);
    const [toDateInvalid, setToDateInvalid] = useState(false);
    const [fromDateValidErr, setFromDateValidErr] = useState(null);
    const [toDateValidErr, setToDateValidErr] = useState(null);
    const [headerLabels, setHeaderLabels] = useState<Record<string, string>>({});
    const [userStatsGridState, setUserStatsGridState] = useState<IUserStatsGridState>({
        userStats: [],
        gridColumns: []
    });
    const { userStats, gridColumns } = userStatsGridState;
    const statisticsDataServiceeObj = useMemo(() => new StatisticsDataService(), []);
    const gridRef = useRef<any>(null);
    const sampleHeaders = (): Record<string, string> => ({
        date1Header: 'Date 1',
        date2Header: 'Date 2',
        date3Header: 'Date 3',
        date4Header: 'Date 4',
        date5Header: 'Date 5'
    });

    useEffect(() => {
        initializeGrid();
        loadUserStats();
    }, [statisticsDataServiceeObj]);    
    
    const initializeGrid = () => {
        const sampleplaceholder = sampleHeaders();
        const gridColumns = statisticsDataServiceeObj.userGridColumns(sampleplaceholder);
        setUserStatsGridState((prevState) => ({
            ...prevState,
            gridColumns: gridColumns,
            userStats: sampleGridData
        }));
    }

    const loadUserStats = async (fromDate?: Date | string,toDate?: Date | string) => {
        try {
            sampleGridData = [];
            setUserStatsGridState((prevState) => ({
                ...prevState,
                userStats: sampleGridData
            }));

            const { stats, headerLabels } = await statisticsDataServiceeObj.loadUserStats(fromDate, toDate);
            sampleGridData = stats;
            setHeaderLabels(headerLabels);          
            const updatedColHeaders = statisticsDataServiceeObj.userGridColumns(headerLabels);

            setUserStatsGridState((prevState) => ({
                ...prevState,
                userStats: sampleGridData,
                gridColumns: updatedColHeaders
            }));
        }
        catch (error: any) {
            Logger.logError(error?.toString?.() ?? String(error));
        }
      };

    const validateDates = (fromDate, toDate) => {
        if (fromDate && toDate) {
            if (fromDate <= toDate) {
                setFromDateInvalid(false);
                setFromDateValidErr("");
            } else {
                setFromDateInvalid(true);
                setFromDateValidErr("From Date must be greater than To Date");
            }

            if (toDate >= fromDate) {
                setToDateInvalid(false);
                setToDateValidErr("");
            } else {
                setToDateInvalid(true);
                setToDateValidErr("To Date must be less than From Date");
            }
        }
    };

    const handleFromDateChange = date => {
        setFromDate(date);
        validateDates(date, toDate);
    };

    const handleToDateChange = date => {
        setToDate(date);
        validateDates(fromDate, date);
    };

    const isButtonEnabled = fromDate && toDate;

    const handleSearchClick = async () => {
        try {            
            loadUserStats(fromDate, toDate);            
        }
        catch (error) {
            Logger.logError(error.toString());
        }
    }

    const handleExportData = async () => {
        try {
            // Get filtered rows from Grid
            const selectedRows = gridRef.current?.getSelectedRows() || [];
            const filteredData = selectedRows.length > 0 
                ? selectedRows.map(row => row.original)
                : userStats;

            // Map data to export format
            return filteredData.map(stat => {
                const exportRow = {};
                gridColumns.forEach(col => {
                    if (col.accessor && col.Header) {
                        exportRow[col.Header] = stat[col.accessor] || '';
                    }
                });
                return exportRow;
            });
        } catch (error) {
            Logger.logError('Export error: ' + error.toString());
            return [];
        }
    };

    const FallbackComponent = ({ error, resetErrorBoundary }) => {
        return (
            <div>
                <h1>An error occurred: {error.message}</h1>
                <button onClick={resetErrorBoundary}>Try again</button>
            </div>
        );
    };

    return (
        <ErrorBoundary FallbackComponent={FallbackComponent}>
            <Container fluid>
                <Navbar className="bg-transparent">
                    <Navbar.Brand>{label.Statistics.USER_STATISTICS_HEADER}</Navbar.Brand>
                </Navbar>
                <Column lg={12}>
                    <Row className='mb-4'>
                        <Column md={3}>
                            <DateTimePicker
                                tabIndex={1}
                                selected={fromDate}
                                onChange={handleFromDateChange}
                                className={`statsDatePicker ${fromDateInvalid ? "is-invalid" : ""}`}
                                minTime={new Date(1990, 0, 1)}
                                maxDate={new Date()}
                                placeholderText={label.Statistics.FROM_DATE}
                                isInvalid={fromDateInvalid == true}
                                invalidWarning={fromDateValidErr ? fromDateValidErr : ''}
                            />
                        </Column>
                        <Column md={3}>
                            <DateTimePicker
                                tabIndex={2}
                                selected={toDate}
                                onChange={handleToDateChange}
                                className={`statsDatePicker ${toDateInvalid ? "is-invalid" : ""}`}
                                minTime={new Date(1990, 0, 1)}
                                maxDate={new Date()}
                                isInvalid={toDateInvalid == true}
                                invalidWarning={toDateValidErr ? toDateValidErr : ''}
                                placeholderText={label.Statistics.TO_DATE}
                            />
                        </Column>
                        <Column md={3}>
                            <Button variant="primary" key="filter"
                                onClick={handleSearchClick}
                                tabIndex={3}
                                id="btnStatsSearch"
                                disabled={!isButtonEnabled}
                            >
                                {label.Statistics.GET_RESULTS}
                            </Button>
                            <ExportToCsv
                                onExport={handleExportData}
                                headers={gridColumns.filter(col => col.accessor && col.Header).map(col => col.Header)}
                                fileName={`UserStatistics_${CommonUtility.formateDate(fromDate, "MMDDYYYY")}_${CommonUtility.formateDate(toDate, "MMDDYYYY")}.csv`}
                                buttonLabel="EXPORT"
                                className="btn btn-primary export-btn-margin"
                                disabled={userStats.length === 0}
                            />
                        </Column>                        
                    </Row>
                    <Row>
                        <div className="">
                            <Card className='p-0'>
                                <Grid
                                    ref={gridRef}
                                    data={userStats}
                                    showFilters={true}
                                    id={'userStatsGrid'}
                                    retainGridState={true}
                                    columns={gridColumns}
                                    hiddenColumns={['selection']}
                                    sortDefault={[]}
                                    grdTableContainerClass="p-0 table-container-campaign"
                                    grdTabelClass="grd-table table-striped table-hover table-sm"
                                    zindexHeader={10000}
                                />
                            </Card>
                        </div>
                    </Row>
                </Column>
            </Container>
        </ErrorBoundary>
    );
}
export default UserStatisticsDashboard;